
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Backdrop,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";

import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import DeleteOutlineOutlinedIcon from "@material-ui/icons/DeleteOutlineOutlined";

import notify from "../../../utils/notifier";
import DocuwareTemplateService from "../../../services/DocuwareTemplateService";
import PaginationRoot from "../PaginationRoot";
import DeleteFolderDialog from "../../../components/configuration/DeleteFolderDialog";

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: "#F5F3F5",
    borderRadius: 12,
    // padding: "24px",
    height: "calc(100vh - 130px)",
    fontFamily: '"Montserrat", sans-serif',
  },
  title: {
    fontWeight: 700,
    fontSize: 22,
    color: "#1E293B",
    marginBottom: 20,
  },
  toolbarRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    // marginBottom: 24, 
    padding: "24px 24px",
    backgroundColor: "white"
  },
  searchField: {
    width: 280,
    backgroundColor: "#FFFFFF",
    borderRadius: 4,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      fontSize: 14,
      color: "#1E293B",
      backgroundColor: "#FFFFFF",
      "& fieldset": {
        borderColor: "#E0E0E0",
      },
      "&:hover fieldset": {
        borderColor: "#BDBDBD",
      },
    },
    "& .MuiOutlinedInput-input": {
      backgroundColor: "#FFFFFF",
      padding: "10px 14px",
    },
  },
  newFolderButton: {
    backgroundColor: "#00445D",
    color: "#FFFFFF",
    borderRadius: "24px",
    padding: "8px 20px",
    textTransform: "none",
    fontWeight: 600,
    "&:hover": { backgroundColor: "#03374A" },
  },

  tableWrapper: {
    background: "#FFFFFF",
    // borderRadius: 8,
    overflow: "hidden",
    boxShadow: "none",
    borderTop: "1px solid #D5D5DD",
    // border: "1px solid #E5E7EB",
  },

  table: {
    minWidth: 700,
    borderCollapse: "collapse",
    "& th": {
      backgroundColor: "#FFFFFF",
      color: "#1E293B",
      fontWeight: 600,
      fontSize: 14,
      fontFamily: '"Montserrat", sans-serif !important',
      textAlign: "left",
      borderBottom: "1px solid #6E6E72",
      padding: "16px 24px",
    },
    "& td": {
      fontSize: 14,
      fontFamily: '"Montserrat", sans-serif !important',
      fontWeight: 400,
      color: "#1E293B",
      borderBottom: "1px solid #E5E7EB",
      padding: "16px 24px",
      verticalAlign: "middle",
    },
    "& tbody tr:nth-of-type(odd)": {
      backgroundColor: "#FFFFFF",
    },
    "& tbody tr:nth-of-type(even)": {
      backgroundColor: "#F9FCFF",
    },
    "& tbody tr:hover": {
      backgroundColor: "#F3F4F6",
    },
  },

  actionCell: {
    textAlign: "center",
    width: 80,
  },
  actionIcon: {
    color: "#4F4F4F",
    "&:hover": { color: "#00445D" },
  },
  emptyRow: {
    color: "#9CA3AF",
    textAlign: "center",
    padding: "24px",
  },
}));

const columns = [
  { id: "name", label: "Name" },
  { id: "createdBy", label: "Created By" },
  { id: "createdOn", label: "Created On" },
  { id: "actions", label: "Actions" },
];

export default function Folders({ currentCompany }) {
  const classes = useStyles();
  const history = useHistory();

  const [rows, setRows] = useState([]);
  const [queryCount, setQueryCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [backdrop, setBackdrop] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState(null);

  const fetchData = async (pageSize = rowsPerPage, currentPage = 0, search = "") => {
    setBackdrop(true);
    try {
      const query = {
        _page: currentPage + 1,
        _limit: pageSize,
        _order: "createdAt",
      };
      if (search && search.length > 2) query["q"] = encodeURIComponent(search);

      const response = await DocuwareTemplateService.getAllTemplates(query);
      let templateResponse = response.data ? response.data.documentTypes : [];
      const total = parseInt(response.headers["x-total-count"] || 0);
      const data = templateResponse.map((item) => ({
        id: item.id,
        name: item.name,
        createdBy: "-",
        createdOn: new Date(item.createdAt).toLocaleDateString("en-GB"),
      }));
      setRows(data);
      setQueryCount(total);
    } catch {
      notify({ type: "error", message: "Failed to load folders" });
    } finally {
      setBackdrop(false);
    }
  };

  useEffect(() => {
    fetchData(rowsPerPage, 0);
  }, [currentCompany]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchData(rowsPerPage, newPage, searchText);
  };

  const handleChangeRowsPerPage = (event) => {
    const val = +event.target.value;
    setRowsPerPage(val);
    setPage(0);
    fetchData(val, 0, searchText);
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchText(val);
    setPage(0);
    fetchData(rowsPerPage, 0, val);
  };

  const handleNewFolderClick = () => {
    history.push("/document-suite/configurations/storage/newfolder");
  };

  const handleEditClick = (id) => {
    history.push(`/document-suite/configurations/storage/update/${id}`);
  };

  const openDeleteDialog = (id) => {
    setSelectedFolderId(id);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setSelectedFolderId(null);
    setDeleteDialogOpen(false);
  };

  const handleDeleteFolder = async (id) => {
    setBackdrop(true);
    try {
      await DocuwareTemplateService.deleteDocumentType(id);
      notify({ type: "success", message: "Folder deleted successfully" });
      fetchData(rowsPerPage, page);
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Failed to delete folder";
      notify({ type: "error", message: errorMessage });
    } finally {
      setBackdrop(false);
      closeDeleteDialog();
    }
  };

  return (
    <Box className={classes.root}>
      <Backdrop open={backdrop} style={{ color: "#00445D", zIndex: 1200 }}>
        <CircularProgress color="inherit" />
      </Backdrop>


      <Box className={classes.toolbarRow}>
        <TextField
          variant="outlined"
          size="small"
          value={searchText}
          onChange={handleSearchChange}
          placeholder="Search Folder"
          className={classes.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ color: "#6B7280" }} />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          className={classes.newFolderButton}
          onClick={handleNewFolderClick}
        >
          New Folder
        </Button>
      </Box>

      <div className={classes.tableWrapper}>
        <TableContainer>
          <Table className={classes.table} stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={col.id}>{col.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.length ? (
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    {columns.map((col) => (
                      <TableCell
                        key={col.id}
                        align={col.id === "actions" ? "center" : "left"}
                        className={col.id === "actions" ? classes.actionCell : undefined}
                      >
                        {col.id === "actions" ? (
                          <Box display="flex" justifyContent="center" alignItems="center">
                            <IconButton
                              size="small"
                              className={classes.actionIcon}
                              onClick={() => handleEditClick(row.id)}
                              disabled
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              className={classes.actionIcon}
                              onClick={() => openDeleteDialog(row.id)}
                            >
                              <DeleteOutlineOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          row[col.id] || "-"
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className={classes.emptyRow}>
                    No records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <PaginationRoot
        queryCount={queryCount}
        page={page}
        handleChangePage={handleChangePage}
        rowsPerPage={rowsPerPage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
      />

      <DeleteFolderDialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={handleDeleteFolder}
        folderId={selectedFolderId}
      />
    </Box>
  );
}
